import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { NgClass } from '@angular/common';
import {
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  Token
} from '@stripe/stripe-js';
import { from, of, Subscription, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { UserRequest } from '@generativ/much-smarter-models';

import { FormService } from '../../services/form.service';
import { FeedbackComponent } from '../../feedback/feedback/feedback.component';
import { FeedbackService } from '../../feedback/feedback.service';
import { AuthService, EmbeddedSignUpRequest } from '../auth.service';
import { PaymentService } from '../payment.service';
import { StudentService } from '../../../pages/student/student.service';
import { ActivatedRoute } from '@angular/router';
import { PhoneInputComponent } from '../../../shared/phone-input/phone-input.component';

const PARENT_ORIGIN = 'https://go.muchsmarter.com';
const TRIAL_STARTED_MESSAGE = 'much-smarter:trial-started';

@Component({
    selector: 'app-billing',
    templateUrl: './billing.component.html',
    styleUrls: ['./billing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, NgClass, ReactiveFormsModule, FeedbackComponent, PhoneInputComponent]
})
export class BillingComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('cardNumber', { static: true }) cardNumberContainer: ElementRef<HTMLElement>;
  @ViewChild('cardExpiry', { static: true }) cardExpiryContainer: ElementRef<HTMLElement>;
  @ViewChild('cardCvc', { static: true }) cardCvcContainer: ElementRef<HTMLElement>;

  billingForm: UntypedFormGroup;
  stripeReady = false;
  stripeFormError: string | null = null;
  stripeToken: Token | null = null;
  isSubmitting = false;
  signupComplete = false;

  private accountCreated = false;
  private loggedIn = false;
  private profileUpdated = false;
  private cardNumber: StripeCardNumberElement | null = null;
  private cardExpiry: StripeCardExpiryElement | null = null;
  private cardCvc: StripeCardCvcElement | null = null;
  private readonly subscriptions = new Subscription();
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private readonly stripeFieldsComplete = {
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false
  };

  constructor(
    public formService: FormService,
    private readonly authService: AuthService,
    private readonly feedbackService: FeedbackService,
    private readonly paymentService: PaymentService,
    private readonly studentService: StudentService,
    private readonly changeDetector: ChangeDetectorRef
  ) {
    this.billingForm = this.formService.createForm(this.formOptions);
  }

  ngOnInit(): void {
    this.activatedRoute.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const email = params.get('email');
        const firstName = params.get('firstname');
        const lastName = params.get('lastname');
        const phone = params.get('phone');
        const prefilledValues: Record<string, string> = {};

        if (email !== null) {
          prefilledValues.email = email.trim();
        }
        if (firstName !== null) {
          prefilledValues.firstName = firstName.trim();
        }
        if (lastName !== null) {
          prefilledValues.lastName = lastName.trim();
        }
        if (phone !== null) {
          const normalizedPhone = phone.startsWith(' ')
            ? `+${phone.trim()}`
            : phone.trim();
          prefilledValues.phoneNumber = normalizedPhone;
        }

        this.billingForm.patchValue(prefilledValues);
        this.changeDetector.markForCheck();
      });
  }

  async ngAfterViewInit(): Promise<void> {
    try {
      await this.paymentService.initStripe();
      this.mountStripeElements();
      this.stripeReady = true;
    } catch {
      this.stripeFormError = 'Payment fields could not be loaded. Please refresh and try again.';
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.cardNumber?.destroy();
    this.cardExpiry?.destroy();
    this.cardCvc?.destroy();
  }

  submitForm(): void {
    if (this.isSubmitting || this.signupComplete) {
      return;
    }

    if (!this.formService.validateFormAndUpdateErrors(this.billingForm)) {
      return;
    }

    if (!this.stripeReady || !Object.values(this.stripeFieldsComplete).every(Boolean)) {
      this.stripeFormError = 'Complete all card details before continuing.';
      this.changeDetector.markForCheck();
      return;
    }

    this.stripeFormError = null;

    const phoneNumber = parsePhoneNumberFromString(this.billingForm.value.phoneNumber);
    if (!phoneNumber?.isValid()) {
      this.billingForm.get('phoneNumber')?.setErrors({ phoneNumber: true });
      return;
    }

    const request: EmbeddedSignUpRequest = {
      email: this.billingForm.value.email.trim(),
      password: this.billingForm.value.password,
      firstName: this.billingForm.value.firstName.trim(),
      lastName: this.billingForm.value.lastName.trim(),
      phoneNumber: phoneNumber.number
    };
    const loginRequest = new UserRequest.Login();
    loginRequest.email = request.email;
    loginRequest.password = request.password;

    this.isSubmitting = true;
    this.feedbackService.cleanState();
    const stripeTokenRequest = this.stripeToken
      ? of(this.stripeToken)
      : from(this.createStripeToken()).pipe(
        tap(stripeToken => {
          this.stripeToken = stripeToken;
        })
      );
    const authenticationRequest = this.loggedIn
      ? of(this.authService.token)
      : this.accountCreated
        ? this.authService.signIn(loginRequest).pipe(
          tap(() => {
            this.loggedIn = true;
          })
        )
      : this.authService.signUpEmbedded(request).pipe(
        tap(() => {
          this.accountCreated = true;
        }),
        switchMap(() => this.authService.signIn(loginRequest)),
        tap(() => {
          this.loggedIn = true;
        })
      );

    this.subscriptions.add(
      stripeTokenRequest.pipe(
        switchMap(stripeToken => authenticationRequest.pipe(
          map(() => stripeToken)
        )),
        switchMap(stripeToken => {
          const profileRequest = this.profileUpdated
            ? of(null)
            : this.studentService.updateEmbeddedProfile({
              firstName: request.firstName,
              lastName: request.lastName,
              phone: request.phoneNumber
            }).pipe(
              tap(() => {
                this.profileUpdated = true;
              })
            );

          return profileRequest.pipe(map(() => stripeToken));
        }),
        switchMap(stripeToken => this.paymentService.startTrial(
          stripeToken.id,
          this.billingForm.value.nameOnCard.trim()
        ).pipe(
          catchError(error => {
            this.stripeToken = null;
            return throwError(() => error);
          })
        ))
      ).subscribe({
        next: student => {
          this.isSubmitting = false;
          this.authService.setCurrentStudent(student);
          this.signupComplete = true;
          this.feedbackService.success(
            'Your account has been created and your trial has started.'
          );
          this.changeDetector.markForCheck();
          window?.postMessage(
            { type: TRIAL_STARTED_MESSAGE },
            '*'
          );
          // for testing purposes
          window.parent.postMessage({ type: TRIAL_STARTED_MESSAGE }, 'https://cdpn.io');
          
          // Published Go page
          window.top?.postMessage({ type: TRIAL_STARTED_MESSAGE }, PARENT_ORIGIN);
        },
        error: error => {
          this.isSubmitting = false;
          const message = typeof error === 'string'
            ? error
            : error?.message ?? 'Unable to secure payment details.';
          this.feedbackService.error(message);
          this.changeDetector.markForCheck();
        }
      })
    );
  }

  private get formOptions(): Record<string, unknown> {
    return {
      firstName: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      lastName: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      email: [
        null,
        Validators.compose([
          Validators.required,
          Validators.email
        ])
      ],
      phoneNumber: [
        '+1',
        Validators.compose([
          Validators.required
        ])
      ],
      password: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      nameOnCard: [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(100)
        ])
      ],
      // addressLine1: [
      //   null,
      //   Validators.compose([
      //     Validators.required
      //   ])
      // ],
      // addressLine2: [null],
      // city: [
      //   null,
      //   Validators.compose([
      //     Validators.required
      //   ])
      // ],
      // state: [null],
      // postalCode: [
      //   null,
      //   Validators.compose([
      //     Validators.required
      //   ])
      // ],
      // country: [
      //   null,
      //   Validators.compose([
      //     Validators.required,
      //     Validators.pattern(/^[A-Za-z]{2}$/)
      //   ])
      // ] 
    };
  }

  private async createStripeToken(): Promise<Token> {
    if (!this.cardNumber) {
      throw new Error('Card details are not ready.');
    }

    const result = await this.paymentService.stripe.createToken(this.cardNumber, {
      name: this.billingForm.value.nameOnCard.trim(),
      // address_line1: this.billingForm.value.addressLine1.trim(),
      // address_line2: this.billingForm.value.addressLine2?.trim() || undefined,
      // address_city: this.billingForm.value.city.trim(),
      // address_state: this.billingForm.value.state?.trim() || undefined,
      // address_zip: this.billingForm.value.postalCode.trim(),
      // address_country: this.billingForm.value.country.trim().toUpperCase()
    });

    if (result.error) {
      throw new Error(result.error.message);
    }
    if (!result.token) {
      throw new Error('Stripe did not return a payment token.');
    }

    return result.token;
  }

  private mountStripeElements(): void {
    const style = {
      base: {
        color: '#495057',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSize: '16px',
        '::placeholder': {
          color: '#6c757d'
        }
      },
      invalid: {
        color: '#dc3545'
      }
    };

    this.cardNumber = this.paymentService.elements.create('cardNumber', {
      showIcon: true,
      style
    });
    this.cardExpiry = this.paymentService.elements.create('cardExpiry', { style });
    this.cardCvc = this.paymentService.elements.create('cardCvc', { style });

    this.cardNumber.mount(this.cardNumberContainer.nativeElement);
    this.cardExpiry.mount(this.cardExpiryContainer.nativeElement);
    this.cardCvc.mount(this.cardCvcContainer.nativeElement);

    this.cardNumber.on('change', event => this.handleStripeChange('cardNumber', event));
    this.cardExpiry.on('change', event => this.handleStripeChange('cardExpiry', event));
    this.cardCvc.on('change', event => this.handleStripeChange('cardCvc', event));
  }

  private handleStripeChange(
    field: keyof typeof this.stripeFieldsComplete,
    event: { complete: boolean; error?: { message: string } }
  ): void {
    this.stripeFieldsComplete[field] = event.complete;
    this.stripeFormError = event.error?.message ?? null;
    this.changeDetector.markForCheck();
  }
}
